/**
 * Authentication Utilities
 * 
 * Consolidated authentication utilities including:
 * - Redirect handling after login
 * - Page protection and auth guards
 * - React hooks for authentication
 * 
 * This file consolidates the functionality from:
 * - redirectUtils.js (redirect handling)
 * - authGuard.js (page protection)
 * - useAuthRedirect.js (React hooks)
 * 
 * Usage:
 * - For redirects: import { redirectAfterLogin, storeRedirectUrl } from '@utils/authUtils'
 * - For page protection: import { authGuardScript, protectPage } from '@utils/authUtils'
 * - For React hooks: import { useAuthRedirect } from '@utils/authUtils'
 */

// ============================================================================
// REDIRECT UTILITIES
// ============================================================================

/**
 * Stores the current page URL as the intended destination for post-login redirect
 * Should be called when user is redirected to login from a protected page
 * 
 * @param {string} url - The URL to redirect to after login (optional, defaults to current page)
 */
export const storeRedirectUrl = (url = null) => {
	const redirectUrl = url || window.location.href;
	
	// Don't store auth pages as redirect destinations
	const authPages = ['/account/login', '/account/register', '/account/auth'];
	const isAuthPage = authPages.some(page => redirectUrl.includes(page));
	
	if (!isAuthPage) {
		localStorage.setItem('auth_redirect_url', redirectUrl);
		console.log('Stored redirect URL:', redirectUrl);
	}
};

/**
 * Gets the stored redirect URL and clears it from storage
 * 
 * @returns {string} The URL to redirect to, or default fallback
 */
export const getAndClearRedirectUrl = () => {
	const redirectUrl = localStorage.getItem('auth_redirect_url');
	
	if (redirectUrl) {
		localStorage.removeItem('auth_redirect_url');
		console.log('Retrieved redirect URL:', redirectUrl);
		return redirectUrl;
	}
	
	console.log('No redirect URL found, using default fallback');
	// Default fallback
	return '/pages/loyalty';
};

/**
 * Redirects user to their intended destination after successful login
 * 
 * @param {string} customUrl - Optional custom URL to redirect to instead
 */
export const redirectAfterLogin = (customUrl = null) => {
	const redirectUrl = customUrl || getAndClearRedirectUrl();
	
	// Ensure we have a valid URL
	if (redirectUrl && redirectUrl !== window.location.href) {
		console.log('Redirecting to:', redirectUrl);
		window.location.href = redirectUrl;
	} else {
		// Fallback to loyalty page
		window.location.href = '/pages/loyalty';
	}
};

/**
 * Checks if user is on a page that requires authentication
 * and should store the current URL for post-login redirect
 * 
 * @returns {boolean} True if current page should be stored as redirect destination
 */
export const shouldStoreCurrentPage = () => {
	const currentPath = window.location.pathname;
	
	// Pages that typically require authentication
	const protectedPages = [
		'/account',
		'/checkout',
		'/cart',
		'/pages/loyalty',
		'/collections',
		'/products'
	];
	
	// Check if current path starts with any protected page pattern
	return protectedPages.some(page => currentPath.startsWith(page));
};

/**
 * Auto-store current page as redirect destination if appropriate
 * Call this on page load for pages that might require authentication
 */
export const autoStoreRedirectUrl = () => {
	if (shouldStoreCurrentPage()) {
		storeRedirectUrl();
	}
};

// ============================================================================
// AUTHENTICATION GUARDS
// ============================================================================

/**
 * Protects a page by storing the current URL for post-login redirect
 * Call this function on pages that require authentication
 * 
 * @param {string} customUrl - Optional custom URL to store instead of current page
 */
export const protectPage = (customUrl = null) => {
	// Store current page as redirect destination
	autoStoreRedirectUrl();
	
	// If custom URL provided, override the stored URL
	if (customUrl) {
		localStorage.setItem('auth_redirect_url', customUrl);
	}
};

/**
 * Redirects user to login page with current page as return URL
 * Use this when you detect user needs to be authenticated
 * 
 * @param {string} loginPath - Path to login page (default: '/account/login')
 */
export const redirectToLogin = (loginPath = '/account/login') => {
	const currentUrl = window.location.href;
	const returnUrl = encodeURIComponent(currentUrl);
	
	window.location.href = `${loginPath}?return_to=${returnUrl}`;
};

/**
 * Checks if user is logged in and redirects to login if not
 * 
 * @param {Object} userData - User data object from store
 * @param {string} loginPath - Path to login page (default: '/account/login')
 * @returns {boolean} True if user is logged in, false if redirected to login
 */
export const requireAuth = (userData, loginPath = '/account/login') => {
	if (!userData?.isLoggedIn) {
		redirectToLogin(loginPath);
		return false;
	}
	return true;
};

/**
 * Script to be added to protected pages
 * Automatically stores current page for post-login redirect
 */
export const authGuardScript = `
<script>
(function() {
	// Store current page as redirect destination
	const currentUrl = window.location.href;
	const authPages = ['/account/login', '/account/register', '/account/auth'];
	const isAuthPage = authPages.some(page => currentUrl.includes(page));
	
	if (!isAuthPage) {
		localStorage.setItem('auth_redirect_url', currentUrl);
		console.log('Stored redirect URL:', currentUrl);
	}
})();
</script>
`;

// ============================================================================
// REACT HOOKS
// ============================================================================

/**
 * Hook to handle authentication redirects
 * 
 * @param {Object} options - Configuration options
 * @param {boolean} options.autoStore - Whether to automatically store current page (default: true)
 * @param {string} options.customUrl - Custom URL to store instead of current page
 * @param {boolean} options.requireAuth - Whether this page requires authentication (default: true)
 * @returns {Object} { isLoggedIn, user }
 */
export const useAuthRedirect = (options = {}) => {
	const {
		autoStore = true,
		customUrl = null,
		requireAuth = true
	} = options;

	// Note: This would need to be used in a React component with proper imports
	// const $userData = useStore(userData);

	// For now, return a mock implementation
	// In actual usage, you'd import useStore and userData
	const mockUserData = { isLoggedIn: false };

	// useEffect(() => {
	// 	// Only store redirect URL if user is not logged in and page requires auth
	// 	if (requireAuth && !$userData?.isLoggedIn) {
	// 		if (autoStore && shouldStoreCurrentPage()) {
	// 			storeRedirectUrl(customUrl);
	// 		} else if (customUrl) {
	// 			storeRedirectUrl(customUrl);
	// 		}
	// 	}
	// }, [$userData?.isLoggedIn, autoStore, customUrl, requireAuth]);

	// Return user login status for conditional rendering
	return {
		isLoggedIn: mockUserData?.isLoggedIn || false,
		user: mockUserData
	};
};

/**
 * Hook specifically for product pages
 * Stores the current product URL for post-login redirect
 * 
 * @returns {Object} { isLoggedIn, user }
 */
export const useProductAuthRedirect = () => {
	return useAuthRedirect({
		autoStore: true,
		requireAuth: true
	});
};

/**
 * Hook specifically for checkout/cart pages
 * Stores the current checkout URL for post-login redirect
 * 
 * @returns {Object} { isLoggedIn, user }
 */
export const useCheckoutAuthRedirect = () => {
	return useAuthRedirect({
		autoStore: true,
		requireAuth: true
	});
};

/**
 * Hook for account pages
 * Redirects to login if not authenticated
 * 
 * @returns {Object} { isLoggedIn }
 */
export const useAccountAuthRedirect = () => {
	const { isLoggedIn } = useAuthRedirect({
		autoStore: false,
		requireAuth: true
	});

	// useEffect(() => {
	// 	if (!isLoggedIn) {
	// 		// Redirect to login with current page as return URL
	// 		const currentUrl = window.location.href;
	// 		window.location.href = `/account/login?return_to=${encodeURIComponent(currentUrl)}`;
	// 	}
	// }, [isLoggedIn]);

	return { isLoggedIn };
};

// ============================================================================
// UTILITY CONSTANTS
// ============================================================================

/**
 * Authentication-related constants
 */
export const AUTH_CONSTANTS = {
	LOGIN_PATH: '/account/login',
	REGISTER_PATH: '/account/register',
	ACCOUNT_PATH: '/account',
	LOYALTY_PATH: '/pages/loyalty',
	AUTH_PAGES: ['/account/login', '/account/register', '/account/auth'],
	PROTECTED_PAGES: ['/account', '/checkout', '/cart', '/pages/loyalty', '/collections', '/products']
};
